import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Archive, LogOut, Moon, Sun, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth, useStacks } from '@/lib/hooks';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function SettingsPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { isPro, archivedStacks, restoreStack, deleteStack, freeTierLimit } = useStacks();

    const [isDark, setIsDark] = React.useState(
        document.documentElement.classList.contains('dark')
    );

    const toggleTheme = () => {
        document.documentElement.classList.toggle('dark');
        setIsDark(!isDark);
    };

    const handleRestore = async (stackId, stackName) => {
        try {
            await restoreStack(stackId);
            toast(`${stackName} restored`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async (stackId, stackName) => {
        try {
            await deleteStack(stackId);
            toast(`${stackName} deleted`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/auth');
    };

    return (
        <div className="max-w-lg mx-auto px-4 py-6">
            <header className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            </header>

            {/* Account */}
            <Card className="mb-3">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                        <User className="w-4 h-4" />
                        Account
                    </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                    <p className="text-sm text-foreground mb-1">
                        {user?.name || 'User'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {user?.email}
                    </p>
                </CardContent>
            </Card>

            {/* Pro section */}
            <Card className="mb-3">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                        <Crown className={cn(
                            "w-4 h-4",
                            isPro ? "text-warning" : "text-muted-foreground"
                        )} />
                        Pro Subscription
                    </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                    <p className="text-sm text-muted-foreground mb-3">
                        {isPro
                            ? "You have Pro access"
                            : `Free tier: ${freeTierLimit} stacks. Upgrade for unlimited.`
                        }
                    </p>
                    {!isPro && (
                        <Button size="sm" disabled>
                            Coming Soon
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* Appearance */}
            <Card className="mb-3">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                        {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        Appearance
                    </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="dark-mode" className="text-sm">
                            Dark mode
                        </Label>
                        <Switch
                            id="dark-mode"
                            checked={isDark}
                            onCheckedChange={toggleTheme}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Archived stacks */}
            <Card className="mb-3">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                        <Archive className="w-4 h-4" />
                        Archived Stacks
                    </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                    {archivedStacks.length > 0 ? (
                        <div className="space-y-2">
                            {archivedStacks.map(stack => {
                                const icon = STACK_ICONS.find(i => i.name === stack.icon) || STACK_ICONS[0];
                                const color = STACK_COLORS.find(c => c.name === stack.color) || STACK_COLORS[0];

                                return (
                                    <div key={stack.id} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                                        <div className={cn(
                                            "w-8 h-8 rounded flex items-center justify-center text-base",
                                            color.lightClass
                                        )}>
                                            {icon.emoji}
                                        </div>
                                        <span className="flex-1 text-sm font-medium truncate">{stack.name}</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRestore(stack.id, stack.name)}
                                            className="h-7 px-2 text-xs"
                                        >
                                            Restore
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Delete {stack.name}?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        This cannot be undone.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        onClick={() => handleDelete(stack.id, stack.name)}
                                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                    >
                                                        Delete
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No archived stacks
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Logout */}
            <Card>
                <CardContent className="py-4">
                    <Button
                        variant="outline"
                        onClick={handleLogout}
                        className="w-full gap-2"
                    >
                        <LogOut className="w-4 h-4" />
                        Log out
                    </Button>
                </CardContent>
            </Card>

            <div className="mt-6 text-center text-xs text-muted-foreground">
                <p>Stacks App • v1.0.0</p>
            </div>
        </div>
    );
}
